import type { CSSProperties, ReactNode } from 'react';
import { AlertTriangle, Hourglass, Lock, RotateCw, SearchX } from 'lucide-react';
import type { UseQueryResult } from '@tanstack/react-query';
import { ApiError, errorMessage } from '../api/client';
import { Button } from './Button';

export function Skeleton({ width, height = 14, radius, style, className }: { width?: number | string; height?: number | string; radius?: number; style?: CSSProperties; className?: string }) {
  return (
    <span
      className={['ui-skeleton', className].filter(Boolean).join(' ')}
      style={{ width: width ?? '100%', height, ...(radius !== undefined && { borderRadius: radius }), ...style }}
      aria-hidden
    />
  );
}

/** A stack of skeleton rows for list-shaped content. */
export function SkeletonRows({ rows = 5, label = 'Loading' }: { rows?: number; label?: string }) {
  return (
    <div role="status" aria-label={label} style={{ display: 'grid', gap: 0 }}>
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px', borderBottom: i < rows - 1 ? '1px solid var(--divider)' : 0 }}
        >
          <Skeleton width={28} height={28} radius={8} />
          <div style={{ flex: 1, display: 'grid', gap: 8 }}>
            <Skeleton width={`${46 - ((i * 7) % 18)}%`} height={12} />
            <Skeleton width={`${28 + ((i * 11) % 22)}%`} height={10} />
          </div>
          <Skeleton width={72} height={22} radius={999} />
        </div>
      ))}
    </div>
  );
}

type StateProps = {
  icon?: ReactNode;
  title: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  compact?: boolean;
  tone?: 'default' | 'error' | 'soon';
};

export function EmptyState({ icon, title, children, actions, compact, tone = 'default' }: StateProps) {
  const cls = ['ui-state', compact && 'ui-state--compact', tone !== 'default' && `ui-state--${tone}`].filter(Boolean).join(' ');
  return (
    <div className={cls}>
      {icon && <div className="ui-state__icon">{icon}</div>}
      <p className="ui-state__title">{title}</p>
      {children && <div className="ui-state__body">{children}</div>}
      {actions && <div className="ui-state__actions">{actions}</div>}
    </div>
  );
}

/** "This part of CertiHash isn't live on this server yet." */
export function ComingSoon({ what, compact }: { what: string; compact?: boolean }) {
  return (
    <EmptyState icon={<Hourglass />} tone="soon" title={`${what} is coming soon`} compact={compact}>
      This server doesn’t offer {what.toLowerCase()} yet. It will appear here automatically once it’s deployed.
    </EmptyState>
  );
}

export function ErrorState({ error, onRetry, what = 'this', compact }: { error: unknown; onRetry?: () => void; what?: string; compact?: boolean }) {
  if (error instanceof ApiError) {
    if (error.isUnavailable) return <ComingSoon what={capitalize(what)} compact={compact} />;
    if (error.isForbidden) {
      return (
        <EmptyState icon={<Lock />} title="Admins only" compact={compact}>
          You need the admin role in this organization to see {what}.
        </EmptyState>
      );
    }
    if (error.isNotFound) {
      return (
        <EmptyState icon={<SearchX />} title="Not found" compact={compact}>
          {capitalize(error.message)}. It may have been removed, or it belongs to another organization.
        </EmptyState>
      );
    }
  }
  return (
    <EmptyState
      icon={<AlertTriangle />}
      tone="error"
      title={`Couldn’t load ${what}`}
      compact={compact}
      actions={
        onRetry && (
          <Button variant="secondary" size="sm" icon={<RotateCw />} onClick={onRetry}>
            Try again
          </Button>
        )
      }
    >
      <span role="alert">{errorMessage(error)}</span>
    </EmptyState>
  );
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Render a query's three states with one call: skeleton while pending, a
 * designed error (including "coming soon" for undeployed routes), then data.
 */
export function QueryView<T>({
  query,
  loading,
  what,
  compact,
  children,
}: {
  query: UseQueryResult<T>;
  loading: ReactNode;
  what: string;
  compact?: boolean;
  children: (data: T) => ReactNode;
}) {
  if (query.isPending) return <>{loading}</>;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} what={what} compact={compact} />;
  return <>{children(query.data)}</>;
}
